import { signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';

import { auth, db } from '../../../firebase';
import { createSocialUser, setTermsStepOpen } from '../../../firebase/user';
import TermsCheckbox, { SIGN_UP_DOCUMENTS, TERMS_ERROR } from '../TermsCheckbox';
import { Content, ContentWrapper, ErrorMessage, Header, SubmitButton, SubTitle, SwitchModeLink, Title } from './styles';

// Billing B4.4: Google and Facebook make the sign-in account before we can ask
// about the terms. A new account sees this step; its user record is written
// only after the tick. Copy approved by Braeden, 2026-10-31.

/**
 * Signs in with Google or Facebook (`signIn` returns Firebase's result).
 * Returns { user, isNew }. For a new account the terms step stays open until
 * TermsStep finishes or is cancelled.
 */
export async function socialSignIn(signIn) {
  setTermsStepOpen(true);
  try {
    const { user } = await signIn();
    const snap = await getDoc(doc(db, 'users', user.uid));
    if (snap.exists()) setTermsStepOpen(false);
    return { user, isNew: !snap.exists() };
  } catch (error) {
    setTermsStepOpen(false);
    throw error;
  }
}

export default function TermsStep({ user, onDone, onCancel }) {
  const [checked, setChecked] = useState(false);
  const [termsError, setTermsError] = useState('');
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const finished = useRef(false);

  // Closed without the tick (Cancel, the X, or a click outside): the sign-in
  // account was made a moment ago, so Firebase lets us delete it.
  useEffect(
    () => () => {
      if (finished.current) return;
      setTermsStepOpen(false);
      user.delete().catch(() => signOut(auth));
    },
    [user]
  );

  const onCheck = (value) => {
    setChecked(value);
    setTermsError('');
  };

  const onCreate = async () => {
    if (!checked) {
      setTermsError(TERMS_ERROR);
      return;
    }
    setIsSaving(true);
    setError('');
    try {
      await createSocialUser(user);
      finished.current = true;
      setTermsStepOpen(false);
      onDone();
    } catch (err) {
      console.error('B4.4: could not create the user record', err);
      setIsSaving(false);
      setError('We could not create your account. Please try again.');
    }
  };

  return (
    <ContentWrapper>
      <Content>
        <Header>
          <div>
            <Title>One more step</Title>
            <SubTitle>Agree to the terms to finish creating your account.</SubTitle>
          </div>
        </Header>
        <TermsCheckbox id="terms-step" checked={checked} onChange={onCheck} documents={SIGN_UP_DOCUMENTS} error={termsError} />
        {!!error && <ErrorMessage>{error}</ErrorMessage>}
        <SubmitButton type="button" disabled={isSaving} value={isSaving ? 'Loading...' : 'Create account'} onClick={onCreate} />
        <SwitchModeLink>
          <button type="button" onClick={onCancel}>
            Cancel
          </button>
        </SwitchModeLink>
      </Content>
    </ContentWrapper>
  );
}

TermsStep.propTypes = {
  user: PropTypes.object.isRequired,
  onDone: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
};
